import { useCallback, useEffect, useState } from "react";
import { Sparkles, Plus, Trash2, Pencil } from "lucide-react";
import { Select } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import useAppraisalRole from "../../hooks/useAppraisalRole";
import {
    APPRAISAL_COMPETENCIES, APPRAISAL_COMPETENCY_FRAMEWORKS, EMPLOYEE_DESIGNATION,
} from "../../constants/api";

const CATEGORIES = [
    { value: "core", label: "Core" },
    { value: "leadership", label: "Leadership" },
    { value: "functional", label: "Functional" },
    { value: "clinical", label: "Clinical" },
    { value: "behavioural", label: "Behavioural" },
];

const EMPTY_COMPETENCY = { name: "", description: "", category: "core", anchors: {} };
const EMPTY_FRAMEWORK = { name: "", description: "", designation_ids: [], competencies: [] };

/**
 * The competency library and the frameworks built from it.
 *
 * Two tabs rather than two pages: a framework is meaningless without the
 * library, and HR builds both in the same sitting.
 */

/* Master endpoints go through tools.utils.paginator, which returns a bare list
   only when `drop_down` is set and a {result, total_count} object otherwise.
   Ask for the list, and unwrap defensively anyway -- a shape mismatch should
   degrade to an empty dropdown, not take the page down with it. */
const asList = (data) => (Array.isArray(data) ? data : data?.result || []);

const CompetencyManager = () => {
    const { fetch, create, update, remove } = useApiMethods();
    const { isHR } = useAppraisalRole();

    const [tab, setTab] = useState("library");
    const [competencies, setCompetencies] = useState([]);
    const [frameworks, setFrameworks] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [cForm, setCForm] = useState(null);     // null = closed
    const [fForm, setFForm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, f] = await Promise.all([
                fetch(APPRAISAL_COMPETENCIES),
                fetch(APPRAISAL_COMPETENCY_FRAMEWORKS),
            ]);
            setCompetencies(c?.data || []);
            setFrameworks(f?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load competencies");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        load();
        fetch(EMPLOYEE_DESIGNATION, { drop_down: "True" })
            .then((r) => setDesignations(asList(r?.data)))
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    // --- competency library ---------------------------------------------

    const saveCompetency = async () => {
        if (!cForm.name.trim()) {
            ToastMsg("error", "Give the competency a name");
            return;
        }
        setSaving(true);
        try {
            const body = { ...cForm, name: cForm.name.trim() };
            const res = cForm.id
                ? await update(APPRAISAL_COMPETENCIES, body)
                : await create(APPRAISAL_COMPETENCIES, body);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Saved");
                setCForm(null);
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not save");
            }
        } catch (_) {
            ToastMsg("error", "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const removeCompetency = async (row) => {
        const res = await remove(`${APPRAISAL_COMPETENCIES}?id=${row.id}`);
        if (res?.status === "success") await load();
        else ToastMsg("error", res?.message || "Could not remove");
    };

    const setAnchor = (level, text) =>
        setCForm((f) => ({ ...f, anchors: { ...(f.anchors || {}), [level]: text } }));

    // --- frameworks ------------------------------------------------------

    const saveFramework = async () => {
        if (!fForm.name.trim()) {
            ToastMsg("error", "Give the framework a name");
            return;
        }
        setSaving(true);
        try {
            const body = {
                ...fForm,
                name: fForm.name.trim(),
                competencies: fForm.competencies.map((c, i) => ({
                    competency_id: c.competency_id,
                    expected_level: c.expected_level === "" ? null : Number(c.expected_level),
                    display_order: i,
                })),
            };
            const res = fForm.id
                ? await update(APPRAISAL_COMPETENCY_FRAMEWORKS, body)
                : await create(APPRAISAL_COMPETENCY_FRAMEWORKS, body);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Saved");
                setFForm(null);
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not save");
            }
        } catch (_) {
            ToastMsg("error", "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const removeFramework = async (row) => {
        const res = await remove(`${APPRAISAL_COMPETENCY_FRAMEWORKS}?id=${row.id}`);
        if (res?.status === "success") await load();
        else ToastMsg("error", res?.message || "Could not remove");
    };

    if (!isHR) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">
                    Only HR can manage the competency library.
                </div>
            </ContainerCard>
        );
    }

    const chosen = new Set((fForm?.competencies || []).map((c) => c.competency_id));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Sparkles}
                title="Competencies"
                subtitle="How work gets done, as against what gets delivered"
                totalRecords={tab === "library" ? competencies.length : frameworks.length}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex gap-2">
                    {[["library", "Library"], ["frameworks", "Frameworks"]].map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setTab(key)}
                            className={cn("px-3 py-1.5 text-sm rounded-md border",
                                tab === key
                                    ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 border-transparent"
                                    : "border-border hover:bg-gray-50 dark:hover:bg-gray-800")}>
                            {label}
                        </button>
                    ))}
                    <div className="flex-1" />
                    {tab === "library" ? (
                        <Button size="sm" variant="brand" onClick={() => setCForm({ ...EMPTY_COMPETENCY })}>
                            <Plus className="w-4 h-4 mr-1" /> Add competency
                        </Button>
                    ) : (
                        <Button size="sm" variant="brand" onClick={() => setFForm({ ...EMPTY_FRAMEWORK, competencies: [] })}>
                            <Plus className="w-4 h-4 mr-1" /> Add framework
                        </Button>
                    )}
                </div>

                {/* --- competency form --- */}
                {tab === "library" && cForm && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1">Name</label>
                                <Input value={cForm.name}
                                    onChange={(e) => setCForm({ ...cForm, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Category</label>
                                <Select className="w-full" value={cForm.category}
                                    onChange={(v) => setCForm({ ...cForm, category: v })}
                                    options={CATEGORIES} />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">Description</label>
                            <Textarea rows={2} value={cForm.description || ""}
                                onChange={(e) => setCForm({ ...cForm, description: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium mb-1">
                                What each level looks like
                            </label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Optional, but this is what makes two managers&rsquo; &ldquo;3&rdquo;
                                mean the same thing. Leave a level blank to skip it.
                            </p>
                            <div className="space-y-1">
                                {[1, 2, 3, 4, 5].map((level) => (
                                    <div key={level} className="flex items-center gap-2">
                                        <span className="w-5 text-xs text-muted-foreground">{level}</span>
                                        <Input className="flex-1"
                                            placeholder={`Behaviour at level ${level}`}
                                            value={(cForm.anchors || {})[level] || ""}
                                            onChange={(e) => setAnchor(level, e.target.value)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="brand" disabled={saving} onClick={saveCompetency}>
                                {cForm.id ? "Update" : "Create"}
                            </Button>
                            <Button size="sm" variant="ghost" disabled={saving}
                                onClick={() => setCForm(null)}>Cancel</Button>
                        </div>
                    </div>
                )}

                {/* --- framework form --- */}
                {tab === "frameworks" && fForm && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium mb-1">Name</label>
                                <Input value={fForm.name}
                                    onChange={(e) => setFForm({ ...fForm, name: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Designations</label>
                                <Select mode="multiple" allowClear className="w-full"
                                    placeholder="Leave blank to apply to everyone"
                                    value={fForm.designation_ids}
                                    onChange={(v) => setFForm({ ...fForm, designation_ids: v })}
                                    options={designations.map((d) => ({
                                        value: d.id, label: d.designation_name || d.name,
                                    }))} />
                                <p className="text-xs text-muted-foreground mt-1">
                                    A framework naming someone&rsquo;s designation wins over a blank one.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium mb-1">Competencies</label>
                            <div className="space-y-2">
                                {fForm.competencies.map((row, idx) => {
                                    const meta = competencies.find((c) => c.id === row.competency_id);
                                    return (
                                        <div key={row.competency_id} className="flex items-center gap-2">
                                            <span className="flex-1 text-sm">{meta?.name || "—"}</span>
                                            <div className="w-36">
                                                <Input type="number" min={1} placeholder="Expected level"
                                                    value={row.expected_level ?? ""}
                                                    onChange={(e) => setFForm({
                                                        ...fForm,
                                                        competencies: fForm.competencies.map((r, i) =>
                                                            i === idx ? { ...r, expected_level: e.target.value } : r),
                                                    })} />
                                            </div>
                                            <button type="button" className="text-red-500 hover:text-red-600"
                                                onClick={() => setFForm({
                                                    ...fForm,
                                                    competencies: fForm.competencies.filter((_, i) => i !== idx),
                                                })}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            <Select showSearch optionFilterProp="label" className="w-full mt-2"
                                placeholder="Add a competency" value={null}
                                onChange={(v) => setFForm({
                                    ...fForm,
                                    competencies: [...fForm.competencies, { competency_id: v, expected_level: "" }],
                                })}
                                options={competencies.filter((c) => !chosen.has(c.id))
                                    .map((c) => ({ value: c.id, label: `${c.name} (${c.category})` }))} />
                        </div>

                        <div className="flex gap-2">
                            <Button size="sm" variant="brand" disabled={saving} onClick={saveFramework}>
                                {fForm.id ? "Update" : "Create"}
                            </Button>
                            <Button size="sm" variant="ghost" disabled={saving}
                                onClick={() => setFForm(null)}>Cancel</Button>
                        </div>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                    {loading ? (
                        <div className="text-center py-10 text-muted-foreground">Loading…</div>
                    ) : tab === "library" ? (
                        competencies.length === 0 ? (
                            <div className="text-center py-10 text-muted-foreground">
                                No competencies yet. Add a few, then group them into a framework.
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {competencies.map((row) => (
                                    <div key={row.id} className="border border-border rounded-lg p-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <div className="font-medium text-sm">{row.name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {row.category}
                                                    {row.anchors && Object.keys(row.anchors).length > 0
                                                        ? ` · ${Object.keys(row.anchors).length} level(s) described`
                                                        : " · no behavioural anchors"}
                                                </div>
                                                {row.description && (
                                                    <p className="text-xs mt-1">{row.description}</p>
                                                )}
                                            </div>
                                            <div className="flex gap-1">
                                                <button type="button" className="text-muted-foreground hover:text-foreground"
                                                    onClick={() => setCForm({ ...row, anchors: row.anchors || {} })}>
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button type="button" className="text-red-500 hover:text-red-600"
                                                    onClick={() => removeCompetency(row)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : frameworks.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">
                            No frameworks yet.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {frameworks.map((row) => (
                                <div key={row.id} className="border border-border rounded-lg p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-medium text-sm">{row.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {row.is_universal
                                                    ? "Applies to everyone"
                                                    : row.designations.map((d) => d.name).join(", ")}
                                                {` · ${row.competencies.length} competenc${row.competencies.length === 1 ? "y" : "ies"}`}
                                            </div>
                                            <div className="text-xs mt-1">
                                                {row.competencies.map((c) =>
                                                    `${c.name}${c.expected_level ? ` (${c.expected_level})` : ""}`).join(" · ")}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button type="button" className="text-muted-foreground hover:text-foreground"
                                                onClick={() => setFForm({
                                                    id: row.id,
                                                    name: row.name,
                                                    description: row.description || "",
                                                    designation_ids: row.designations.map((d) => d.id),
                                                    competencies: row.competencies.map((c) => ({
                                                        competency_id: c.competency_id,
                                                        expected_level: c.expected_level ?? "",
                                                    })),
                                                })}>
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button type="button" className="text-red-500 hover:text-red-600"
                                                onClick={() => removeFramework(row)}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
};

export default CompetencyManager;
